// The coverage surfaces: one chip, one week, one glance (bead hf-e6-commitments-coverage-udt.6).
//
// Pure components over `coverageData.ts`. Copy from `@herefor/product-language`,
// components from `@herefor/design-system`, and not one number computed here.
//
// ── WHY THERE IS A `CoverageChip` AT ALL ───────────────────────────────────────
//
// The five states already render in three places (the plan week, the need detail,
// the need list) and each grew its own `Record<UserVisibleState, StateChipState>`.
// Three copies of an exhaustive map are three chances for the same underlying state
// to read differently on two screens — which is precisely the failure this bead's
// acceptance criterion names. This is the one that new surfaces build on, and
// `coverageSurfaces.test.tsx` holds the cross-surface proof that the existing three
// still agree with it.
//
// ── THREE RULES THE CHIP KEEPS (plan §10.1) ────────────────────────────────────
//
// 1. COLOUR SUPPORTS, NEVER REPLACES. The chip carries its label as text; the state
//    token only tints it. `StateChip` is built that way and the tests assert the
//    words, not the colour.
// 2. A PENDING ASK NEVER MAKES A NEED LOOK SAFER. "Mary has been asked" is secondary
//    context UNDER an unchanged primary state, and the sentence itself ends with
//    "This is not covered yet." (REQ-003, plan §10.1, §14.5).
// 3. PARTIALITY IS PEOPLE, NEVER TIME. "1 of 2 people confirmed" and nothing else —
//    no bar, no percentage, no per-hour segments (NED-012, COV-006).

import {
  Button,
  Card,
  EmptyState,
  StateChip,
  StatusMessage,
  type StateChipState,
} from "@herefor/design-system";
import {
  CANONICAL_VOCABULARY,
  COVERAGE_SURFACE_MESSAGES as COPY,
  EMPTY_STATES,
  STATUS_CONTEXT,
  USER_VISIBLE_STATES,
  type UserVisibleState,
} from "@herefor/product-language";
import type { ReactNode } from "react";

import type { CoverageDayView, CoverageWeekView, NeedCoverageView, PlanHealthView } from "./coverageData";
import { planHealthTone } from "./coverageData";

/**
 * User-visible state → the chip's state token.
 *
 * Exhaustive, so a new state cannot fall through to a default colour.
 */
const CHIP_STATE: Record<UserVisibleState, StateChipState> = {
  needsSomeone: "needsSomeone",
  covered: "covered",
  pleaseRecheck: "pleaseRecheck",
  done: "done",
  cancelled: "cancelled",
};

/* ================================================================== the chip === */

export interface CoverageChipProps {
  readonly coverage: NeedCoverageView;
}

/**
 * One need's state, count and Past treatment — the canonical rendering.
 *
 * "Needs someone · 1 of 2 people confirmed" reads as one thought, which is why the
 * count sits inside the same paragraph rather than on a line of its own.
 */
export function CoverageChip({ coverage }: CoverageChipProps) {
  const copy = USER_VISIBLE_STATES[coverage.stateKey];

  return (
    <p className="coverage-chip" data-state={coverage.stateKey}>
      <StateChip label={copy.label} state={CHIP_STATE[coverage.stateKey]} />
      {coverage.counts === null ? null : (
        // COV-006: no count on a single-person need. "1 of 1 person confirmed"
        // beside a chip already reading Covered is noise dressed as precision.
        <span className="coverage-chip__count">
          {" · "}
          {STATUS_CONTEXT.coverageCount(coverage.counts.confirmed, coverage.counts.required)}
        </span>
      )}
      {coverage.isPast ? <StateChip label={STATUS_CONTEXT.past} state="past" /> : null}
    </p>
  );
}

export interface PendingAskProps {
  /**
   * Structurally the one field this component reads, so any surface with the names can
   * pass them (bead hf-9pw).
   *
   * It took the whole `NeedCoverageView`, which meant the need DETAIL — whose own
   * coverage view is deliberately narrower — could not use it and would have had to
   * write the sentence a second time. One sentence, one place: widening the input is
   * what keeps it that way.
   */
  readonly coverage: { readonly awaitingReplyNames: readonly string[] };
}

/**
 * "Mary has been asked — waiting for reply. This is not covered yet."
 *
 * Rendered UNDER the chip, never inside it, and the primary state above is unchanged
 * (REQ-003). The sentence names the person when exactly one has been asked, because
 * "somebody has been asked" is not something an organiser can act on. With two or
 * more it falls back to the unnamed explanation rather than composing a list here —
 * this module does not write sentences.
 */
export function PendingAsk({ coverage }: PendingAskProps) {
  const asked = coverage.awaitingReplyNames;
  if (asked.length === 0) return null;

  const first = asked[0];
  const message =
    asked.length === 1 && first !== undefined
      ? STATUS_CONTEXT.askedPersonWaiting(first)
      : STATUS_CONTEXT.askedExplanation;

  return (
    <StatusMessage tone="info">
      <span className="coverage-pending-ask">{message}</span>
    </StatusMessage>
  );
}

/* ============================================================== plan health === */

export interface PlanHealthProps {
  readonly health: PlanHealthView;
}

/**
 * The organiser's glance: what is handled, what needs chasing.
 *
 * A headline sentence and a tally of NEEDS. Deliberately not a dashboard — there is
 * no score, no percentage, no comparison with last week, and the copy catalogue
 * carries the reasoning. The tally's labels come from `CANONICAL_VOCABULARY`, the
 * same words the chips use, so the glance and the week cannot end up calling one
 * state two things.
 */
export function PlanHealth({ health }: PlanHealthProps) {
  const tone = planHealthTone(health);

  return (
    <Card title={COPY.planHealthTitle} titleLevel={2}>
      <div className="coverage-health" data-tone={tone}>
        {tone === "nothingPlanned" ? <p>{COPY.nothingPlanned}</p> : null}
        {tone === "allHandled" ? <p>{COPY.allHandled}</p> : null}
        {tone === "outstanding" ? (
          <>
            {/* Ordered by what an organiser can do something about. Both sentences
                appear when both are true: a week can have a gap AND a promise at
                risk, and showing only one would hide the other. */}
            {health.needingSomeone > 0 ? <p>{COPY.needingSomeone(health.needingSomeone)}</p> : null}
            {health.awaitingRecheck > 0 ? (
              <p>{COPY.awaitingRecheck(health.awaitingRecheck)}</p>
            ) : null}
          </>
        ) : null}

        {health.totalNeeds === 0 ? null : (
          <ul className="coverage-health__tally">
            <li>{COPY.tally(CANONICAL_VOCABULARY.needsSomeone, health.needingSomeone)}</li>
            <li>{COPY.tally(CANONICAL_VOCABULARY.pleaseRecheck, health.awaitingRecheck)}</li>
            <li>{COPY.tally(CANONICAL_VOCABULARY.covered, health.covered)}</li>
          </ul>
        )}
      </div>
    </Card>
  );
}

/* ================================================================== the week === */

export interface CoverageWeekProps {
  readonly week: CoverageWeekView;
  /**
   * Moving between weeks (bead hf-rtf).
   *
   * Absent means the week is fixed, which is what the glance on Today wants: a
   * "this week summary" that could be paged to another week would stop being a
   * summary of this one. Present means the reader may move, and `isCurrentWeek`
   * decides whether the heading says "This week" or names the week it is showing.
   */
  readonly onChangeWeek?: (step: -1 | 1) => void;
  readonly onReturnToThisWeek?: () => void;
  readonly isCurrentWeek?: boolean;
  readonly renderNeedAction?: (need: NeedCoverageView, dateLabel: string) => ReactNode;
  /** The week title is the page heading on the live coverage route. */
  readonly headingLevel?: 1 | 2;
}

/**
 * Seven days, every one of them present.
 *
 * An empty Thursday is INFORMATION — nobody is needed — and dropping it would make
 * the reader count days to work out which one they are looking at. So the empty day
 * says so in words rather than being a gap.
 *
 * This does not reuse the design system's `CalendarWeek`: that primitive models a day
 * as three named blocks (morning/afternoon/evening) for Epic 5's availability entry,
 * and a need has its own window rather than a block. Bucketing needs into blocks to
 * fit the component would invent a placement the server never made.
 */
export function CoverageWeek({
  week,
  onChangeWeek,
  onReturnToThisWeek,
  isCurrentWeek = true,
  renderNeedAction,
  headingLevel = 2,
}: CoverageWeekProps) {
  const Heading = headingLevel === 1 ? "h1" : "h2";
  return (
    <section
      aria-label={COPY.weekRegionLabel(week.weekStartLabel)}
      className="coverage-week"
      data-current-week={String(isCurrentWeek)}
      data-week-start={week.weekStartLocal}
    >
      {/* "This week" only when it IS this week. A heading that said so over a week
          three weeks out is the specific wrongness bead hf-rtf exists to remove; the
          region label beneath it has always named the week, and now the heading does
          too whenever they would disagree. */}
      <Heading className="coverage-week__heading">
        {isCurrentWeek ? COPY.weekHeading : COPY.weekOfHeading(week.weekStartLabel)}
      </Heading>

      {onChangeWeek === undefined ? null : (
        // A nav rather than a toolbar: these are movements between views of the same
        // thing, and a screen reader announcing "navigation" is telling the truth.
        <nav aria-label={COPY.weekRegionLabel(week.weekStartLabel)} className="coverage-week__nav">
          <Button
            onClick={() => {
              onChangeWeek(-1);
            }}
            variant="secondary"
          >
            {COPY.previousWeek}
          </Button>
          {isCurrentWeek || onReturnToThisWeek === undefined ? null : (
            // Offered only when it would do something. A "back to this week" button on
            // this week is a control that has to be read before it can be ignored.
            <Button onClick={onReturnToThisWeek} variant="secondary">
              {COPY.thisWeekAction}
            </Button>
          )}
          <Button
            onClick={() => {
              onChangeWeek(1);
            }}
            variant="secondary"
          >
            {COPY.nextWeek}
          </Button>
        </nav>
      )}
      <ol className="coverage-week__days">
        {week.days.map((day) => (
          <li key={day.localDate}>
            <CoverageDay
              day={day}
              headingLevel={headingLevel === 1 ? 2 : 3}
              {...(renderNeedAction === undefined ? {} : { renderNeedAction })}
            />
          </li>
        ))}
      </ol>
    </section>
  );
}

function CoverageDay({
  day,
  headingLevel,
  renderNeedAction,
}: {
  readonly day: CoverageDayView;
  readonly headingLevel: 2 | 3;
  readonly renderNeedAction?: (need: NeedCoverageView, dateLabel: string) => ReactNode;
}) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  return (
    <section className="coverage-day" data-date={day.localDate}>
      <Heading className="coverage-day__heading">{day.dayLabel}</Heading>

      {day.needs.length === 0 ? (
        <p className="coverage-day__empty">{COPY.dayNothingNeeded}</p>
      ) : (
        <ul className="coverage-day__needs">
          {day.needs.map((need) => (
            <li className="coverage-need" key={need.supportNeedId}>
              <p className="coverage-need__label">
                {need.href === null ? need.label : <a href={need.href}>{need.label}</a>}
              </p>
              <p className="coverage-need__window">{need.windowLabel}</p>
              <CoverageChip coverage={need} />
              <PendingAsk coverage={need} />
              {renderNeedAction?.(need, day.dayLabel) ?? null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ============================================================ attention list === */

export interface CoverageAttentionProps {
  readonly heading: string;
  /** Already ordered by the server. This surface renders the order it is given. */
  readonly needs: readonly NeedCoverageView[];
}

/**
 * What still wants a person, in the server's order.
 *
 * The ordering is `convex/coverage/read.ts`'s — please-recheck first, then soonest —
 * and is NOT reapplied here. A list that re-sorted what it was handed would be a
 * second opinion about urgency, and the two would diverge silently.
 */
export function CoverageAttention({ heading, needs }: CoverageAttentionProps) {
  return (
    <section aria-labelledby="coverage-attention-heading" className="coverage-attention">
      <h2 id="coverage-attention-heading">{heading}</h2>

      {needs.length === 0 ? (
        <EmptyState
          description={EMPTY_STATES.everythingCovered.body}
          title={EMPTY_STATES.everythingCovered.title}
        />
      ) : (
        <ul className="coverage-attention__list">
          {needs.map((need) => (
            <li className="coverage-need" key={need.supportNeedId}>
              <p className="coverage-need__label">
                {need.href === null ? need.label : <a href={need.href}>{need.label}</a>}
              </p>
              <p className="coverage-need__window">{need.windowLabel}</p>
              <CoverageChip coverage={need} />
              <PendingAsk coverage={need} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
